import { t } from "i18next";
import { Form, Modal, Table } from "react-bootstrap";
import { PlanningDay, planningLevels, PlanningWeek } from "../../../../interfaces/planning";
import moment from "moment";
import { updatePlanningWeek, pastePlanningDay } from "../../../../services/planning";
import { useState, useEffect } from "react";
import CoachPlanningDayRow from "./CoachPlanningDayRow";
import { Training } from "../../../../interfaces/training";
import { getTrainingsBySeason } from "../../../../services/training";
import { LoadingSpinner } from "../../../general/loading-spinner";

interface CopiedDayRef {
    season_id: number;
    team_id: number;
    week_key: string;
    day_key: string;
}

interface CoachPlanningWeekProps {
    team_id: number;
    team_name: string;
    season_id: number;
    season_name: string;
    week: PlanningWeek;
    closeCallback: CallableFunction;
    saveWorkload: CallableFunction;
    saveDay: CallableFunction;
    onRefresh?: CallableFunction;
    copiedDay?: CopiedDayRef | null;
    onCopyDay?: (ref: CopiedDayRef) => void;
}

function CoachPlanningWeek(props: CoachPlanningWeekProps) {
    const [workload, setWorkload] = useState(props.week.workload);
    const [trainings, setTrainings] = useState<Training[]>([]);
    const [trainingsLoaded, setTrainingsLoaded] = useState(false);

    /**
     * Context menu state for copy/paste day
     */
    const [contextMenuVisible, setContextMenuVisible] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [contextMenuDayKey, setContextMenuDayKey] = useState<string | null>(null);

    /**
     * Load trainings master once for the entire week
     * This is more efficient than loading in each day component (7 times)
     */
    useEffect(() => {
        loadTrainings();
    }, [props.season_id]);

    // Close context menu when clicking anywhere
    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenuVisible) {
                setContextMenuVisible(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [contextMenuVisible]);

    const loadTrainings = async () => {
        try {
            setTrainingsLoaded(false);

            // Use optimized endpoint
            const response = await getTrainingsBySeason(props.season_id);

            // Extract all trainings from nested structure
            const allTrainings: Training[] = [];
            response.data.forEach((section: any) => {
                if (section.trainings && Array.isArray(section.trainings)) {
                    const sectionTrainings = section.trainings.map((t: any) => ({
                        ...t,
                        section_name: section.name,
                        section_id: section.id
                    }));
                    allTrainings.push(...sectionTrainings);
                }
            });
            setTrainings(allTrainings);
            setTrainingsLoaded(true);
        } catch (error) {
            console.error('Error loading trainings:', error);
            setTrainingsLoaded(true); // Mark as loaded even on error to avoid infinite loading
        }
    };

    const handleClose = () => {
        props.closeCallback();
    }

    const handleWorkloadChange = (event: any) => {
        const body_obj = {
            team_id: props.team_id,
            season_id: props.season_id,
            week: props.week.key,
            workload: event.currentTarget.value
        }
        updatePlanningWeek(body_obj)
            .then(response => {
                if (response.data.status == 1) {
                    setWorkload(response.data.data.workload);
                    props.saveWorkload(props.week.key, response.data.data.workload);
                } else {
                    alert(t("Data could not be saved. Try again later."));
                }
            });
    }

    const handleSaveDay = (week: string, day: PlanningDay) => {
        props.saveDay(week, day);
    }

    const handleDayContextMenu = (event: React.MouseEvent, dayKey: string) => {
        event.preventDefault();
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
        setContextMenuDayKey(dayKey);
        setContextMenuVisible(true);
    }

    const handleCopyDay = () => {
        if (!contextMenuDayKey) return;

        if (props.onCopyDay) {
            props.onCopyDay({
                season_id: props.season_id,
                team_id: props.team_id,
                week_key: props.week.key,
                day_key: contextMenuDayKey
            });
        }

        setContextMenuVisible(false);
    }

    const handlePasteDay = async () => {
        if (!contextMenuDayKey || !props.copiedDay) return;

        try {
            const pasteBody = {
                season_id: props.copiedDay.season_id,
                team_id: props.copiedDay.team_id,
                week: props.copiedDay.week_key,
                source_day: props.copiedDay.day_key,
                destination_week: props.week.key,
                destination_day: contextMenuDayKey
            };

            const response = await pastePlanningDay(pasteBody);

            if (response.data.status === -1) {
                alert(t("Error") + ": " + response.data.message);
                setContextMenuVisible(false);
                return;
            }

            setContextMenuVisible(false);

            // Refresh planning data via parent callback
            if (props.onRefresh) {
                props.onRefresh();
            }
        } catch (error) {
            console.error('Error pasting day:', error);
            alert(t("Error pasting day. Please try again."));
            setContextMenuVisible(false);
        }
    }

    return (
        <Modal className="coach-planning-week-container" show={ true }
            onHide={ handleClose } size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    { moment(props.week.to).format('MMM YYYY') } - { 'M' + props.week.name }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="planning-week-heading">
                    <div className="team">
                        <div className="label">
                            { t("Team") }
                        </div>
                        <div className="value">
                            { props.team_name }
                        </div>
                    </div>
                    <div className="season">
                        <div className="label">
                            { t("Season") }
                        </div>
                        <div className="value">
                            { props.season_name }
                        </div>
                    </div>
                    <div className="dates">
                        <div className="label">
                            { t("Dates") }
                        </div>
                        <div className="value">
                            { moment(props.week.from).format('DD/MM/y') } { t("to") } { moment(props.week.to).format('DD/MM/y') }
                        </div>
                    </div>
                    <div className="workload">
                        <div className="label">
                            { t("Workload") }
                        </div>
                        <div className="value">
                            <Form.Select aria-label={ t("Workload") } name="workload" tabIndex={ 0 }
                                data-week={ props.week.key } defaultValue={ workload }
                                onChange={ handleWorkloadChange }>
                                <option value="">{ t("- select -") }</option>
                                <option value="40">{ '40%' }</option>
                                <option value="50">{ '50%' }</option>
                                <option value="60">{ '60%' }</option>
                                <option value="70">{ '70%' }</option>
                                <option value="80">{ '80%' }</option>
                                <option value="90">{ '90%' }</option>
                                <option value="100">{ '100%' }</option>
                            </Form.Select>
                        </div>
                    </div>
                </div>
                <div className="planning-week-data-table">
                    {!trainingsLoaded ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingSpinner />
                            <p className="mt-3">{t("Loading trainings...")}</p>
                        </div>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <th colSpan={2} className="heading-day sticky">{ t("Day") }</th>
                                    <th className="heading-type">{ t("Type") }</th>
                                    <th className="heading-pct">{ t("%") }</th>
                                    <th className="heading-description">{ t("Training description") }</th>
                                    {
                                        planningLevels.map((l: any) => (
                                            <th key={l.key} className={ 'heading-level-item level-' + l.key }>{ l.name }</th>
                                        ))
                                    }
                                    <th className="heading-level-item heading-km">{ t("KM") }</th>
                                    <th className="heading-level-item heading-th">{ t("TH") }</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    props.week.days.map((d: PlanningDay, index) => (
                                        <CoachPlanningDayRow key={ d.key }
                                            team_id={ props.team_id}
                                            season_id={ props.season_id }
                                            week={ props.week }
                                            day={ d }
                                            saveDay={ handleSaveDay }
                                            tabIndex={ index }
                                            trainings={ trainings }
                                            trainingsLoaded={ trainingsLoaded }
                                            onDayContextMenu={ handleDayContextMenu }
                                            onRefresh={ props.onRefresh }/>
                                    ))
                                }
                            </tbody>
                        </Table>
                    )}
                </div>
                {/*<div className="planning-week-data">
                    <div className="day-heading">
                        <div className="cells">
                            <div className="day-group">
                                { t("Day") }
                            </div>
                            <div className="type">
                                { t("Type") }
                            </div>
                            <div className="pct">
                                { '%' }
                            </div>
                            <div className="description">
                                { t("Training description") }
                            </div>
                            { planningLevels.map((l: any) => {
                                return <div className={ 'level-item level-' + l.key }>
                                        { l.name }
                                    </div>
                            })}
                        </div>
                    </div>
                    { props.week.days.map((d: PlanningDay, index) => {
                        return <CoachPlanningDay key={ d.key }
                            team_id={ props.team_id} season_id={ props.season_id }
                            week={ props.week } day={ d } saveDay={ handleSaveDay } tabIndex={ index }/>
                    })}
                </div>*/}
                {/* Context menu for copy/paste day */}
                {contextMenuVisible && (
                    <div
                        className="week-context-menu"
                        style={{
                            position: 'fixed',
                            top: contextMenuPosition.y,
                            left: contextMenuPosition.x,
                            zIndex: 10000
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="context-menu-item" onClick={handleCopyDay}>
                            <i className="bi bi-clipboard"></i>
                            <span>{t("Copy Day")}</span>
                        </div>
                        {props.copiedDay && (
                            <div className="context-menu-item" onClick={handlePasteDay}>
                                <i className="bi bi-clipboard-check"></i>
                                <span>{t("Paste Day")}</span>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
        </Modal>);
}

export default CoachPlanningWeek;